import { useState } from "react";
import { CheckCircle2, Send, ExternalLink, ArrowRightCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { QuickQuote } from "@/hooks/useQuickQuotes";
import { QuickQuoteSendSheet } from "./QuickQuoteSendSheet";
import { QuickQuotePromoteDialog } from "./QuickQuotePromoteDialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: QuickQuote | null;
}

const fmtBRL = (n: number | null | undefined) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n ?? 0);

export function QuickQuoteCreatedDialog({ open, onOpenChange, quote }: Props) {
  const [sendOpen, setSendOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  if (!quote) return null;

  const handleOpenPublic = () => {
    if (!quote.public_slug) return;
    window.open(`/p/cotacao/${quote.public_slug}`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mb-2 flex items-center justify-center">
              <div className="rounded-full bg-success/10 p-3">
                <CheckCircle2 className="h-8 w-8 text-success" />
              </div>
            </div>
            <DialogTitle className="text-center">
              Cotação {quote.code} criada!
            </DialogTitle>
            <DialogDescription className="text-center">
              O que deseja fazer agora?
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Cliente</span>
              <span className="font-medium truncate">{quote.client_name || "—"}</span>
            </div>
            <div className="mt-1 flex justify-between gap-2">
              <span className="text-muted-foreground">Valor estimado</span>
              <span className="font-medium">
                {fmtBRL(quote.total_value ?? quote.investment_min)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => setSendOpen(true)}
              className="w-full"
              size="lg"
            >
              <Send className="mr-2 h-4 w-4" />
              Enviar agora
            </Button>
            <Button
              onClick={handleOpenPublic}
              variant="outline"
              className="w-full"
              disabled={!quote.public_slug}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Ver cotação
            </Button>
            <Button
              onClick={() => setPromoteOpen(true)}
              variant="outline"
              className="w-full"
            >
              <ArrowRightCircle className="mr-2 h-4 w-4" />
              Promover para Oportunidade
            </Button>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickQuoteSendSheet
        open={sendOpen}
        onOpenChange={setSendOpen}
        quote={quote}
      />

      <QuickQuotePromoteDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        quote={quote}
        onPromoted={() => {
          setPromoteOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
